/**
 * Constants for filtering rental-related collections from credit reports.
 *
 * These patterns help distinguish rental collections (apartments, housing, etc.)
 * from non-rental (medical bills, student loans, etc.) for the Rental History tab.
 * 
 * SOURCES:
 * - TransUnion codes: background-service/src/utils/transunion.js
 * - TransUnion industry codes: backend/src/providers/transunion/smartmove/dataclasses/credit_report_tradeline.py
 * - Experian data: background-service/test/fixtures/experian/ + production payloads
 * 
 */

/**
 * Keywords that indicate a collection is NOT rental-related.
 * Collections matching these patterns in comments or creditor name will be excluded.
 * 
 * @example
 * // These will be EXCLUDED:
 * { creditorsName: "City Medical Group" }          // Contains "Medical"
 * { creditorsName: "Student Loan Services" }       // Contains "Student"
 * { comments: "Emergency room visit" }             // Contains "Emergency"
 */
export const RENTAL_EXCLUDE_KEYWORDS = [
  "Medical",
  "Hospital",
  "Doctor",
  "Emergency",
  "Surgery",
  "Student",
  "Student Loan",
  "Student Debt",
] as const;

/**
 * Keywords that indicate a collection IS rental-related.
 * Collections matching these patterns in the creditor name will be included.
 * 
 * @example
 * // These will be INCLUDED:
 * { creditorsName: "Sunrise Apartments LLC" }      // Contains "Apartments"
 * { comments: "Rental Debt from unit 4B" }         // Contains "Rental Debt"
 * 
 * // Word boundaries prevent false matches:
 * { creditorsName: "Department Store" }            // ✗ Won't match (no word boundary)
 */
export const RENTAL_INCLUDE_KEYWORDS = [
  "Apartment",
  "Apartments",
  "Apt",
  "Apmt",
  "Apts",
  "Homes",
  "Community",
  "Living",
  "Lease",
  "Leasing",
  "Residence",
  "Residences",
  "House",
  "Live",
  "Rental Debt",
  "Properties",
  "Property",
  "Real Estate",
  "Residential",
  "Tenant",
  "Tenants",
  "Tenancy",
  "Landlord",
  "Condo",
  "HOA",
  "Unit",
  "Loft",
  "Studio",
  "Townhome",
  "Townhouse",
  "Duplex",
  "Triplex",
  "Co-op",
  "Cooperative",
] as const;

/**
 * Escapes special regex characters in a string.
 * Used for building safe regex patterns from user-provided or dynamic strings.
 */
const escapeRegexChars = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Normalizes text for case-insensitive matching (trim + lowercase).
 */
const normalizePhrase = (value: string): string => value.trim().toLowerCase()

/**
 * Loan type codes and descriptions that indicate rental/housing-related agreements.
 * 
 * SOURCE: TransUnion bureau codes from background-service/src/utils/transunion.js
 * Includes both short codes (e.g., "LE", "RA") and human-readable strings (e.g., "Lease", "Rental Agreement")
 */
export const RENTAL_RELATED_LOAN_TYPES = [
  "LE",  // Lease
  "RA",  // Rental Agreement
  "RE",  // Real Estate
  "RL",  // Real Estate—Junior Liens
  "RM",  // Real Estate Mortgage
  "CV",  // Conventional Real Estate Mortgage
  "FL",  // FMHA Real Estate Mortgage
  "FR",  // FHA Real Estate Mortgage
  "HE",  // Home Equity Loan
  "MB",  // Manufactured Housing
  "VM",  // V.A. Real Estate Mortgage
  "VA",  // V.A. Loan
  "Lease",
  "Rental Agreement",
  "Real Estate",
  "Real Estate—Junior Liens",
  "Real Estate Mortgage",
  "Conventional Real Estate Mortgage",
  "FMHA Real Estate Mortgage",
  "FHA Real Estate Mortgage",
  "Home Equity Loan",
  "Manufactured Housing",
  "V.A. Real Estate Mortgage",
  "V.A. Loan",
] as const;

/**
 * Industry codes that indicate rental/housing collections.
 * 
 * SOURCE: backend/src/providers/transunion/smartmove/dataclasses/credit_report_tradeline.py
 * TransUnion: 'R' = Real estate, public accommodations
 */
export const RENTAL_INDUSTRY_CODES = ["R"] as const;

/**
 * Experian-specific account type descriptions for rental/housing.
 * 
 * NOTE: Experian uses human-readable descriptive strings instead of short bureau codes.
 * These are matched using partial string matching with word boundaries.
 */
export const EXPERIAN_RENTAL_ACCOUNT_TYPES = [
  "Lease",
  "Lease Account",
  "Lease/Rental Agreement",
  "Rental/Lease",
  "Rental Contract",
  "Rental Agreement",
  "Real Estate",
  "Real Estate Specific Type Unknown",
  "Real Estate Mortgage",
  "Real Estate—Junior Liens",
  "Conventional Real Estate Loan",
  "Conventional Real Estate Loan, Including Purchase Money First",
  "Conventional Real Estate Mortgage",
  "FHA Real Estate Mortgage",
  "FMHA Real Estate Mortgage",
  "V.A. Real Estate Mortgage",
  "VA Real Estate Mortgage",
  "V.A. Loan",
  "VA Loan",
  "Home Equity Loan",
  "Home Equity Line of Credit",
  "Home Equity",
  "Manufactured Housing",
] as const;

/**
 * Experian-specific KOB (Kind of Business) descriptions for rental/housing.
 */
export const EXPERIAN_RENTAL_KOB_DESCRIPTIONS = [
  "Mortgage Reporters",
  "Real Estate",
  "Real Estate Mortgage",
  "Property Management",
  "Apartment Communities",
  "Landlords",
  "Residential Property",
  "Housing",
] as const;

/**
 * Pre-normalized (lowercased and trimmed) Experian account types for efficient matching.
 */
export const EXPERIAN_RENTAL_ACCOUNT_TYPES_NORMALIZED =
  EXPERIAN_RENTAL_ACCOUNT_TYPES.map(normalizePhrase);

/**
 * Pre-normalized (lowercased and trimmed) Experian KOB descriptions for efficient matching.
 */
export const EXPERIAN_RENTAL_KOB_DESCRIPTIONS_NORMALIZED =
  EXPERIAN_RENTAL_KOB_DESCRIPTIONS.map(normalizePhrase);

/**
 * Creates a case-insensitive regex from keywords.
 * Adds word boundaries to single-word keywords to prevent partial matches.
 * 
 * @param keywords - Array of keywords to match
 * @param useWordBoundaries - Whether to add word boundaries for single-word keywords
 * @returns Compiled RegExp pattern
 */
const createKeywordPattern = (
  keywords: readonly string[],
  useWordBoundaries: boolean = false
): RegExp => {
  const patterns = keywords.map(keyword => {
    const escaped = escapeRegexChars(keyword)
    return useWordBoundaries && !keyword.includes(' ') 
      ? `\\b${escaped}\\b` 
      : escaped
  })

  return new RegExp(patterns.join('|'), 'i')
}

/**
 * Compiled regex patterns for efficient matching.
 * 
 * @example
 * // EXCLUDE pattern examples:
 * RENTAL_COLLECTION_PATTERNS.exclude.test("Medical Center")      // ✓ true (excludes)
 * RENTAL_COLLECTION_PATTERNS.exclude.test("Student Housing")      // ✓ true (excludes) 
 * RENTAL_COLLECTION_PATTERNS.exclude.test("Sunrise Apartments")   // ✗ false (allows)
 * 
 * // INCLUDE pattern examples (with word boundaries):
 * RENTAL_COLLECTION_PATTERNS.include.test("Apt 102 Collections")  // ✓ true (includes)
 * RENTAL_COLLECTION_PATTERNS.include.test("Department Store")     // ✗ false (word boundary)
 * RENTAL_COLLECTION_PATTERNS.include.test("Property Management")  // ✓ true (includes)
 * 
 * // LOAN TYPE pattern examples:
 * RENTAL_COLLECTION_PATTERNS.loanTypes.test("LE")               // ✓ true (Lease)
 * RENTAL_COLLECTION_PATTERNS.loanTypes.test("Lease")            // ✓ true
 * RENTAL_COLLECTION_PATTERNS.loanTypes.test("LEASE")            // ✓ true (case insensitive)
 * RENTAL_COLLECTION_PATTERNS.loanTypes.test("Release")          // ✗ false (word boundary)
 */
export const RENTAL_COLLECTION_PATTERNS = {
  exclude: createKeywordPattern(RENTAL_EXCLUDE_KEYWORDS),
  include: createKeywordPattern(RENTAL_INCLUDE_KEYWORDS, true),
  loanTypes: createKeywordPattern(RENTAL_RELATED_LOAN_TYPES, true),
} as const;
